import React from 'react';

/**
 * App-wide error boundary. React unmounts the entire tree when a render or
 * lifecycle method throws and there is no boundary above it — that is the
 * "white screen" failure mode this portal had. This catches the throw,
 * logs it, and shows a recoverable fallback instead of a blank page.
 *
 * Kept as a self-contained class with inline styles on purpose: it must be
 * able to render even when the theme/route providers (or their persisted
 * state) are the thing that blew up, so it depends on nothing but React.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Flip to the fallback UI on the next render.
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // For now this just reaches the console; wire to Sentry/analytics here
    // when the portal gets real error reporting.
    console.error('Portal crashed:', error, info?.componentStack);
  }

  handleReset = () => {
    // The most common cause of a hard crash here is corrupt persisted state
    // (e.g. a malformed `brella_order`), so clear it before retrying — a plain
    // reload would just re-read the same bad value and crash again.
    try {
      localStorage.removeItem('brella_order');
    } catch (_) {
      /* localStorage may be unavailable (private mode); nothing to clear */
    }
    window.location.assign('/apps/brella');
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          color: '#1a1a1a',
        }}
      >
        <h1 style={{ fontSize: '20px', fontWeight: 700, margin: '0 0 8px' }}>
          Something went wrong
        </h1>
        <p style={{ fontSize: '14px', color: '#555', maxWidth: '420px', margin: '0 0 20px' }}>
          We hit an unexpected error loading your order. Starting over usually
          fixes it. If it keeps happening, please contact support.
        </p>
        <button
          type="button"
          onClick={this.handleReset}
          style={{
            background: '#8661FF',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Start over
        </button>
      </div>
    );
  }
}
